<template>
    <div>
        <v-dialog
            :value="isOpen"
            scrollable
            max-width="900px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        Tworzenie nowego zamówienia
                    </h1>
                </v-card-title>

                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="productTemplate"
                                :label="!productTemplate ? 'Proszę wybrać szablon produktu' : 'Szablon produktu'"
                                :items="productTemplates"
                                item-text="name"
                                return-object
                                no-data-text="Nie znaleziono szablonów produktu"
                                :error-messages="productTemplateErrors"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="impositionProductTypeConfigurationId"
                                :label="!impositionProductTypeConfigurationId ? 'Proszę wybrać szablon wykonania' : 'Szablon wykonania'"
                                :items="productTypeConfigurations"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono szablonów wykonania"
                                :error-messages="productTypeConfigurationErrors"
                                :disabled="!productTemplate"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="12" lg="12">
                            <v-data-table
                                v-model="selectedProducts"
                                :headers="headers"
                                :items="productsList"
                                item-key="name"
                                show-select
                                class="elevation-1"
                            >
                                <template #item.name="{item}">
                                    {{ item.name }}
                                </template>
                                <template #item.format="{item}">
                                    {{ item.format }}
                                </template>
                                <template #item.number="{item}">
                                    {{ item.productFiles.length }}
                                </template>
                                <template #item.amount="{item}">
                                    {{ item.amount }}
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        @click="onClose"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="success"
                        text
                        :loading="actionLoading"
                        :disabled="!selectedProducts.length || !productTemplate || !impositionProductTypeConfigurationId"
                        @click="onSaveOrderByProduct"
                    >
                        Utwórz zamówienie
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>

import tooltipDelay from '@/mixins/tooltip'
import { errorHandler } from '@/utils/errorHandler'
import { required } from 'vuelidate/lib/validators'

export default {
    name: 'CreateOrderByCampaignProductDialog',
    components: {
    },
    mixins: [
        tooltipDelay,
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        products: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            productTemplate: null,
            impositionProductTypeConfigurationId: null,
            productsList: [],
            selectedProducts: [],
            actionLoading: false,
            cancelAmountUpdate: false,
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    sortable: false,
                },
                {
                    text: 'Format',
                    value: 'format',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Zaakceptowane pliki',
                    value: 'number',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    sortable: false,
                    align: 'center'
                },
            ],
        }
    },
    validations() {
        let rules = {
            productTemplate: {
                required,
            },
            impositionProductTypeConfigurationId: {
                required,
            }
        }

        return rules
    },
    computed: {
        productTemplates() {
            return this.$store.state.campaigns.campaigns.productTemplates
        },
        productTypeConfigurations() {
            return this.$store.state.jobs.jobs.productTypeConfigurations
        },
        productTemplateErrors() {
            const errors = []

            if (!this.$v.productTemplate.$dirty) return errors
            !this.$v.productTemplate.required && errors.push('Proszę wybrać szablon produktu.')

            return errors
        },
        productTypeConfigurationErrors() {
            const errors = []

            if (!this.$v.impositionProductTypeConfigurationId.$dirty) return errors
            !this.$v.impositionProductTypeConfigurationId.required && errors.push('Proszę wybrać szablon wykonania.')

            return errors
        },
    },
    watch: {
        isOpen(val) {
            if (!val) {
                this.selectedProducts = []
            } else {
                this.productsList = JSON.parse(JSON.stringify(this.products))

                this.productsList.forEach(product => {
                    product.amount = product.amountLeftToOrder
                })
            }
        },
        productTemplate(newValue) {
            if (newValue) {
                this.fetchProductTypeConfigurations(newValue.productTypeId)
            }
        },
    },
    mounted() {
        this.$store.dispatch('campaigns/getProductTemplates')
    },
    methods: {
        fetchProductTypeConfigurations(id) {
            this.$store.dispatch('jobs/getProductTypeConfigurations', { productTypeId: id })
        },
        onSaveOrderByProduct() {
            if (!this.checkValidation()) {
                return
            }
            this.actionLoading = true
            const payload = {
                productTemplateId: this.productTemplate.id,
                impositionProductTypeConfigurationId: this.impositionProductTypeConfigurationId,
                products: this.selectedProducts.map(product => ({productId: product.id, amount: product.amount}))
            }

            this.$store.dispatch('campaigns/createOrderByProduct', payload)
                .then(response => {
                    this.$emit('create-order-by-product', response)
                    this.onClose()
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading = false
                })
        },
        onClose() {
            this.$emit('on-close')
            setTimeout(() => {
                this.setInitialData()
                this.$v.$reset()
            }, 500)
        },
        checkValidation() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return false
            }

            return true
        },
        setInitialData() {
            this.impositionProductTypeConfigurationId = null
            this.productTemplate = null
        }
    }
}
</script>
